/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';

import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';
import Table from '../../components/table/Table';
import { RootState } from '../../core/state/state';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import TH from '../../components/table/TH/TH';
import THead from '../../components/table/THead/THead';
import Badge from '../components/badge/Badge';
import { isReadAllRole } from '../../core/state/user/selectors';
import Icon from '../../components/icon/Icon';
import { deleteBarrierDevice, getBarriers } from '../../core/state/barriers/asyncActions';
import { Barrier, BarrierDeviceMode, BarrierLocationType } from '../../core/state/barriers/types';
import deliveryZoneColorMap from '../../core/constants/deliveryZoneColorMap';
import Tooltip from '../../components/tooltip/Tooltip';
import { DATE_FORMAT_EU_LONG } from '../../core/constants/date';
import Button from '../../components/form/button/Button';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import EnableMaintenanceModeModal from './modals/enable-maintenance-mode/EnableMaintenanceModeModal';
import ConfirmModal from '../components/confirm-modal/ConfirmModal';
import { spawnGlobalNotification } from '../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../core/enums/GlobalNotificationTypes';
import { NotificationPosition } from '../../core/state/global-notifications/types';
import CreateAndEditBarrierModal from './modals/create-new-barrier/CreateAndEditBarrierModal';
import ActionDropdown from '../../components/action-dropdown/ActionDropdown';
import { BARRIER_LOGS } from '../../core/constants/routes';
import { history } from '../../core/state/history';
import { getBarrierModeColor, getDeliveryZonesBadges } from './helpers';

import './BarrierManagement.scss';

enum ModalType {
  Edit,
  MaintenanceModeModal,
  Closed
}

const BarrierManagement: React.FC = () => {
  const { barriers } = useSelector((state: RootState) => state.barriers);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isUserReader = useSelector(isReadAllRole);

  const [selectedBarrier, setSelectedBarrier] = React.useState<number | null>(null);
  const [modalOpened, setModalOpened] = React.useState<ModalType>(ModalType.Closed);
  const [barrierForDelete, setBarrierForDelete] = React.useState<Barrier | null>(null);

  React.useEffect(() => {
    dispatch(getBarriers());
  }, [dispatch]);

  const handleDeleteModal = async () => {
    if (barrierForDelete) {
      const isProjectType = barrierForDelete.locations[0].type === BarrierLocationType.Project;
      const { serialNumber } = barrierForDelete;
      const response: boolean = await dispatch<any>(deleteBarrierDevice(barrierForDelete.id));
      if (response === false) {
        if (isProjectType) {
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Error,
              postition: NotificationPosition.Right,
              title: t('common.deletionFailed'),
              message: t('barrierManagement.modal.delete.validation.project.text')
            })
          );
        }

        if (isProjectType && barrierForDelete.locations[0].deliveryZoneIndex) {
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Error,
              postition: NotificationPosition.Right,
              title: t('common.deletionFailed'),
              message: t('barrierManagement.modal.delete.validation.zone.text')
            })
          );
        }
      } else {
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Success,
            postition: NotificationPosition.Right,
            title: t('barrierManagement.modal.delete.message.success.title'),
            message: t('barrierManagement.modal.delete.message.success.text', { serialNumber })
          })
        );
      }
    }
    setBarrierForDelete(null);
    dispatch(getBarriers());
  };

  return (
    <TableWrapper>
      <div className="table__header">
        <div className="d-flex justify-content-between w-100">
          <span>{t('barrierManagement.title')}</span>
          <Button onClick={() => setModalOpened(ModalType.Edit)} buttonTypes={['blue', 'small']}>
            <Icon className="bookings-mobile-screen__create-icon" icon="plus" svg />
            {t('common.addNew')}
          </Button>
        </div>
      </div>
      <div className="barrier-management__table">
        <Table>
          <THead>
            <TR>
              <TH>{t('barrierManagement.heading.snControl')}</TH>
              <TH>{t('barrierManagement.heading.snSystem')}</TH>
              <TH>{t('barrierManagement.heading.inventoryNumber')}</TH>
              <TH>{t('barrierManagement.modal.createNew.form.projectAssignment')}</TH>
              <TH>{t('booking.deliveryZone')}</TH>
              <TH>{t('common.status')}</TH>
              <TH>{t('common.description')}</TH>
              <TH />
            </TR>
          </THead>

          <TBody>
            {barriers?.map((barrier: Barrier, i: number) => (
              <TR key={`${i}-${barrier.id}`}>
                <TD className="text-left">
                  <div className="table__text barrier-management__serial-number">{barrier.serialNumber}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{barrier.barrierGateSerialNumber || '--'}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{barrier.inventoryNumber || '--'}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{barrier.locations[0].label || '--'}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">
                    {barrier.locations.length < 4
                      ? barrier.locations.map((item) => (
                          <div className="barrier-management__badge">
                            <Badge
                              key={item.deliveryZoneIndex}
                              color={deliveryZoneColorMap[item.deliveryZoneIndex]}
                              icon={item.deliveryZoneIndex}
                              label={item.deliveryZoneName}
                            />
                          </div>
                        ))
                      : getDeliveryZonesBadges(barrier.locations)}
                  </div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">
                    <Badge
                      color={getBarrierModeColor(barrier.mode)}
                      label={barrier.mode}
                      tooltip={barrier.mode === BarrierDeviceMode.Offline ? format(new Date(barrier.lastPing), DATE_FORMAT_EU_LONG) : barrier.mode}
                    />
                  </div>
                </TD>
                <TD className="text-left">
                  {barrier.description ? (
                    <Tooltip placement="top-start" text={barrier.description}>
                      <div className="barrier-management__text">{barrier.description}</div>
                    </Tooltip>
                  ) : (
                    '--'
                  )}
                </TD>

                <TD>
                  {!isUserReader && (
                    <ActionDropdown>
                      <Dropdown.Item
                        className="barrier-management__action-item"
                        onClick={() => {
                          setSelectedBarrier(barrier.id);
                          setModalOpened(ModalType.Edit);
                        }}
                      >
                        {t('common.edit')}
                      </Dropdown.Item>
                      <Dropdown.Item className="barrier-management__action-item" onClick={() => setBarrierForDelete(barrier)}>
                        {t('common.delete')}
                      </Dropdown.Item>
                      {barrier.mode === BarrierDeviceMode.Online && (
                        <Dropdown.Item
                          className="barrier-management__action-item"
                          onClick={() => {
                            setSelectedBarrier(barrier.id);
                            setModalOpened(ModalType.MaintenanceModeModal);
                          }}
                        >
                          {t('barrierManagement.action.enableMaintenanceMode')}
                        </Dropdown.Item>
                      )}
                      <Dropdown.Item
                        className="barrier-management__action-item"
                        onClick={() => {
                          setSelectedBarrier(barrier.id);
                          history.push(BARRIER_LOGS.replace(':barrierId', barrier.id.toString()));
                        }}
                      >
                        {t('barrierManagement.action.viewLogs')}
                      </Dropdown.Item>
                    </ActionDropdown>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {modalOpened === ModalType.Edit && selectedBarrier === null && (
          <CreateAndEditBarrierModal isOpen={modalOpened === ModalType.Edit} closeModal={() => setModalOpened(ModalType.Closed)} />
        )}
        {modalOpened === ModalType.MaintenanceModeModal && selectedBarrier && (
          <EnableMaintenanceModeModal
            isOpen={modalOpened === ModalType.MaintenanceModeModal}
            closeModal={() => {
              setSelectedBarrier(null);
              setModalOpened(ModalType.Closed);
            }}
            barrierId={selectedBarrier}
          />
        )}
        {selectedBarrier && modalOpened === ModalType.Edit && (
          <CreateAndEditBarrierModal
            isOpen
            closeModal={() => {
              setSelectedBarrier(null);
              setModalOpened(ModalType.Closed);
            }}
            barrierId={selectedBarrier}
          />
        )}
        {barrierForDelete && (
          <ConfirmModal
            size="small"
            confirmText={t('common.yes')}
            cancelText={t('common.no')}
            titleText={t('barrierManagement.modal.delete.title')}
            contentText={t('barrierManagement.modal.delete.text')}
            isOpen
            onCancel={() => setBarrierForDelete(null)}
            onConfirm={() => handleDeleteModal()}
          />
        )}
        {!barriers ||
          (barriers.length === 0 && (
            <NoResultsPage
              title={t('barrierManagement.noBarriers.title')}
              description={t('barrierManagement.noBarriers.text')}
              icon="barrierSystem"
              iconClass="barrier-management__no-results-icon"
            />
          ))}
      </div>
    </TableWrapper>
  );
};

export default BarrierManagement;
